import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { parseShopifyGid } from 'utils'
import { onMetalSurcharge } from 'constants/tagCatalog'

// ─── Add-on pricing (VRG-142) ────────────────────────────────────────────────
// Two price types: setup_fee (flat, quantity-independent — design/QR artwork setup)
// and per_unit_surcharge (scales with quantity — on-metal ferrite material).
//
// VRG-575: the fee is no longer declared here. It lives in `@repo/types` as the
// ONE constant this app, the API and every display component read — it used to
// be four literals, two of which described themselves as mirrors of the API's,
// which is exactly how they end up different.
export { CUSTOMIZATION_SETUP_FEE_USD as CUSTOMIZATION_SETUP_FEE } from '@repo/types'
import { CUSTOMIZATION_SETUP_FEE_USD } from '@repo/types'

/**
 * Minimum case size that can carry a customization.
 *
 * The setup fee covers artwork/plate origination, which the converter will not run for
 * a small case -- so this is a real production floor, not an upsell. Lives here beside
 * the fee because the two are the same commercial fact, and the cart needs it to keep a
 * customization from surviving a downgrade to a smaller case.
 */
export const CUSTOMIZATION_MIN_QTY = 1000

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CartItem {
  /**
   * Encoded as "<numericShopifyProductId>#<numericShopifyVariantId>".
   * Matches the encoding the payment API expects — POST /payments/create-payment-intent
   * decodes via productId.split('#') in shopify-service-provider.ts getVariantsTotal.
   *
   * Both ids are the numeric suffix of the Shopify GIDs
   * (e.g. "gid://shopify/Product/123" → "123", "gid://shopify/ProductVariant/456" → "456").
   * Derived using the existing parseShopifyGid helper (apps/platform/src/utils/parsers.ts).
   */
  productId: string
  variantId: string
  sku: string
  title: string        // product title
  variantTitle: string // variant label e.g. "Case of 100"
  unitPrice: number    // per-unit price (server-computed from unit_price field)
  casePrice: number    // price for one unit of this variant (the "case" price = variant.price)
  quantity: number     // number of cases in the cart
  /** tags per case (variant.quantity, e.g. 100) — drives per-unit add-on surcharges. */
  unitsPerCase?: number
  /** VRG-142 On-Metal (ferrite) add-on — a per-unit surcharge scaling with quantity. */
  onMetal?: boolean
  image?: string
  /**
   * Optional custom-logo/design selections (VERA-831). Structural type so the
   * cart layer stays decoupled from the product page's TagCustomization.
   * NOTE: custom pricing delta + backend order wiring are NOT yet applied —
   * this only carries the buyer's design selections through the cart.
   */
  customization?: {
    color: string
    finish: string
    /** VRG-142 QR add-on — a printed QR alongside the NFC (print/artwork concern, flat setup). */
    qr?: boolean
    logoFinish?: string
    logoDataUrl: string | null
    logoName: string | null
    /** Logo transform on the tag canvas (x/y/scale/rotation as % + source px). */
    logoPlacement?: {
      xPct: number
      yPct: number
      scalePct: number
      rotation: number
      naturalWidth: number
      naturalHeight: number
    } | null
    /** Back face — only present for 2-sided tags (custom.sides). */
    back?: {
      logoDataUrl: string | null
      logoName: string | null
      logoPlacement?: {
        xPct: number
        yPct: number
        scalePct: number
        rotation: number
        naturalWidth: number
        naturalHeight: number
      } | null
    } | null
  }
}

interface CartContextValue {
  items: CartItem[]
  itemCount: number
  addItem(item: CartItem): void
  removeItem(productId: string): void
  setQuantity(productId: string, qty: number): void
  clear(): void
  subtotal: number
}

// ─── Add-on + line-total helpers (VRG-142) ───────────────────────────────────

/**
 * VRG-575 — a local identity for one customised design, used ONLY to group
 * identical designs inside one cart.
 *
 * ⚠️ This is NOT the server's artwork key and does not try to be. The server
 * hashes the decoded logo BYTES (see `artwork-key.ts`), which needs async
 * `crypto.subtle` and is the authority on what gets charged. This is a
 * synchronous, structural comparison, and it is exact for the only job it has:
 * two lines carrying the same design object in one cart produce the same string,
 * so the cart stops showing three setup fees for one logo.
 *
 * Mirrors the server in EXCLUDING placement — see `artwork-key.ts` for why, and
 * for the note that this needs supplier confirmation.
 */
function cartArtworkSignature(item: CartItem): string | null {
  if (!item.customization) return null
  const c = item.customization as Record<string, any>
  return JSON.stringify([
    c.logoDataUrl ?? null,
    c.back?.logoDataUrl ?? null,
    (c.color ?? '').trim().toLowerCase() || null,
    (c.finish ?? '').trim().toLowerCase() || null,
    (c.logoFinish ?? '').trim().toLowerCase() || null,
  ])
}

/**
 * VRG-575 — the cart's setup-fee ESTIMATE: one fee per distinct artwork.
 *
 * Charged once per artwork, so three customised lines carrying one logo are one
 * fee rather than three (which is what the old per-line `cartItemSetupFee` did).
 *
 * ⚠️ An ESTIMATE, and knowingly a HIGH one. The cart cannot know whether this
 * company has already paid setup for a design on a previous order — that is a
 * server-side read against `company_customization_setups`. So a reorder of
 * already-approved artwork shows a fee here and is NOT charged at checkout,
 * where the total comes from the API's own calculation.
 *
 * Erring high rather than low is deliberate: a total that GROWS between the cart
 * and the card form is the one customers experience as a bait and switch.
 */
export function cartSetupFeeTotal(items: CartItem[]): number {
  const artworks = new Set<string>()
  for (const item of items) {
    const signature = cartArtworkSignature(item)
    if (signature) artworks.add(signature)
  }
  return artworks.size * CUSTOMIZATION_SETUP_FEE_USD
}

/** Per-unit surcharge add-ons on a line (on-metal ferrite), scaling with total units. */
export function cartItemPerUnitSurcharge(item: CartItem): number {
  if (!item.onMetal) return 0
  const totalUnits = (item.unitsPerCase ?? 0) * item.quantity
  return onMetalSurcharge(totalUnits)
}

/**
 * Line total: cases × case price + per-unit surcharges.
 *
 * VRG-575: the setup fee is NO LONGER part of a line total, and cannot be —
 * it is charged once per artwork across the whole cart, so it does not belong to
 * any one line. Adding it to each line is what produced the triple charge. It is
 * a cart-level figure now (`cartSetupFeeTotal`) and the subtotal adds it once.
 */
export function cartItemTotal(item: CartItem): number {
  return item.casePrice * item.quantity + cartItemPerUnitSurcharge(item)
}

// ─── Context ──────────────────────────────────────────────────────────────────

const CartContext = createContext<CartContextValue | null>(null)

const STORAGE_KEY = 'vera:tag-shop-cart'

function readStorage(): CartItem[] {
  try {
    if (typeof window === 'undefined') return []
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeStorage(items: CartItem[]): void {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // Ignore storage errors (private browsing, storage full, etc.)
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function CartProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<CartItem[]>(() => readStorage())

  // Persist on every change
  useEffect(() => {
    writeStorage(items)
  }, [items])

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
  // VRG-575: the setup fee is added ONCE for the whole cart, not per line — see
  // cartSetupFeeTotal. Adding it per line is what charged one logo three times.
  const subtotal =
    items.reduce((sum, item) => sum + cartItemTotal(item), 0) + cartSetupFeeTotal(items)

  function addItem(incoming: CartItem): void {
    setItems(prev => {
      const idx = prev.findIndex(i => i.productId === incoming.productId)
      if (idx === -1) {
        return [...prev, incoming]
      }
      // Merge: sum quantities for the same productId
      const updated = [...prev]
      updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + incoming.quantity }
      return updated
    })
  }

  function removeItem(productId: string): void {
    setItems(prev => prev.filter(i => i.productId !== productId))
  }

  function setQuantity(productId: string, qty: number): void {
    if (qty <= 0) {
      removeItem(productId)
      return
    }
    setItems(prev =>
      prev.map(i => (i.productId === productId ? { ...i, quantity: qty } : i))
    )
  }

  function clear(): void {
    setItems([])
  }

  return (
    <CartContext.Provider value={{ items, itemCount, addItem, removeItem, setQuantity, clear, subtotal }}>
      {children}
    </CartContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) {
    throw new Error('useCart must be used inside <CartProvider>')
  }
  return ctx
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/**
 * Builds the productId encoding expected by the payment API.
 * POST /payments/create-payment-intent receives [{productId, quantity}] where
 * productId = "<numericProductId>#<numericVariantId>".
 *
 * Both the product and variant GIDs follow the Shopify pattern
 * "gid://shopify/Product/<id>" / "gid://shopify/ProductVariant/<id>".
 * parseShopifyGid strips the prefix, leaving the numeric id.
 */
export function buildCartProductId(productGid: string, variantGid: string): string {
  // Accept EITHER a full Shopify GID ("gid://shopify/Product/123") OR an
  // already-numeric id ("123"). The tag-shop hooks normalize ids to numeric via
  // parseShopifyGid before they reach here, and parseShopifyGid is slash-anchored
  // (/\/(\d+)$/) — so re-parsing an already-numeric id returns "" and EVERY item
  // collapses to the same "#" key: all variants merge into one cart row and
  // checkout breaks (the payment API splits "#" into empty ids). Extract trailing
  // digits with or without a leading slash instead.
  const numericId = (v: string): string => {
    const m = String(v).match(/(\d+)$/)
    return m ? m[1] : String(v)
  }
  return `${numericId(productGid)}#${numericId(variantGid)}`
}
